import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../../i18n/hooks';
import { useFilter } from '../../filters/context/FilterContext';
import { MARGIN_TYPES } from '../constants/surebetTypes';
import { useMarginData, useEventData } from '../hooks/useDataProcessing';
import { getBookmakerName } from '../utils/surebetUtils';
import DateUtils from '../../../modules/utils/DateUtils';
import NumberUtils from '../../../modules/utils/NumberUtils';
import './SurebetItem.css';

/**
 * SurebetItem - Individual surebet card component
 * Based on Listed_surebets.html mockup
 *
 * Features:
 * - Event details (teams, date, time, championship)
 * - Dynamic margin types display
 * - Coefficients with bookmaker information
 * - Color-coded profit indicators
 * - Calculator button for each margin
 * - Responsive design
 * - Accessibility features
 */
const SurebetItem = ({
  surebet,
  onCalculatorClick,
  onClick,
  className = '',
  showLiveDemoMarginInfo = false,
}) => {
  const { t, language } = useTranslation();
  const { availableBookmakers } = useFilter();

  // Use custom hooks for data processing
  const { maxMargin, availableTypes, isProfitable } = useMarginData(surebet);
  const { formattedDate, formattedTime } = useEventData(surebet?.event, language);

  /**
   * Get bookmaker display name using the same logic as the filter (availableBookmakers list)
   */
  const getBookmakerDisplayName = (bookmakerId) => {
    const fromFilter = availableBookmakers.find(b => b.id === bookmakerId);
    return fromFilter ? fromFilter.name : getBookmakerName(bookmakerId);
  };

  /**
   * Get all available margin types from surebet
   */
  const marginTypes = useMemo(() => {
    if (!surebet.margins || Object.keys(surebet.margins).length === 0) {
      return [];
    }

    // Sort margin types by value (highest first)
    return Object.entries(surebet.margins)
      .sort(([, a], [, b]) => (b.value || 0) - (a.value || 0));
  }, [surebet.margins]);

  /**
   * Get margin type configuration from constants
   */
  const getMarginConfig = (marginKey) => {
    return MARGIN_TYPES[marginKey] || {
      description: { bg: marginKey, en: marginKey },
      outcomes: {}
    };
  };

  /**
   * Get outcome label from coefficient type
   */
  const getOutcomeLabel = (marginKey, coefficientType) => {
    const config = getMarginConfig(marginKey);
    const outcome = config.outcomes[coefficientType];

    if (!outcome) {
      return coefficientType;
    }

    return outcome[language] || outcome.bg || coefficientType;
  };

  /**
   * Get margin type name
   */
  const getMarginTypeName = (marginKey) => {
    const config = getMarginConfig(marginKey);
    return config.description[language] || config.description.bg || marginKey;
  };

  /**
   * Format date for display
   * Uses DateUtils from modules/utils (Component 12)
   */
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return DateUtils.formatDateBG(dateString);
  };


  /**
   * Handle card click
   */
  const handleCardClick = () => {
    if (onClick) {
      onClick(surebet);
    }
  };

  /**
   * Handle calculator button click
   */
  const handleCalculatorClick = (marginKey, event) => {
    event.stopPropagation(); // Prevent card click
    if (onCalculatorClick) {
      onCalculatorClick(surebet, marginKey);
    }
  };

  if (!surebet || !surebet.event) {
    return null;
  }

  return (
    <article
      className={`surebet-item ${className}`}
      role="article"
      aria-label={`${t('surebets.card.ariaLabel')}: ${surebet.event.teamOne} ${t('surebets.card.vs')} ${surebet.event.teamTwo}`}
      onClick={handleCardClick}
    >
      {/* Event Header */}
      <header className="surebet-item__header">
        <div className="surebet-item__event-info">
          <h2 className="surebet-item__title">
            {surebet.event.teamOne} {t('surebets.card.vs')} {surebet.event.teamTwo}
          </h2>
          <p className="surebet-item__subtitle">
            {surebet.event.championship}
          </p>
        </div>
        <div className="surebet-item__event-meta">
          <time className="surebet-item__date" dateTime={surebet.event.date}>
            {formatDate(surebet.event.date)}
          </time>
          <time className="surebet-item__time" dateTime={surebet.event.time}>
            {surebet.event.time}
          </time>
        </div>
      </header>

      {/* Margins Container */}
      {marginTypes.length > 0 && (
        <div className="surebet-item__margins">
          {/* Table Header */}
          <div className="surebet-item__margins-header">
            <div className="surebet-item__header-cell surebet-item__header-cell--market">
              {t('surebets.card.market')}
            </div>
            <div className="surebet-item__header-cell surebet-item__header-cell--odds">
              {t('surebets.card.bookmakerOdds')}
            </div>
            <div className="surebet-item__header-cell surebet-item__header-cell--profit">
              {t('surebets.card.profit')}
            </div>
            <div className="surebet-item__header-cell surebet-item__header-cell--actions">
              {t('surebets.card.actions')}
            </div>
          </div>

          {/* Margin Rows */}
          {marginTypes.map(([marginKey, marginData]) => (
            <MarginRow
              key={marginKey}
              marginKey={marginKey}
              marginData={marginData}
              getMarginTypeName={getMarginTypeName}
              getOutcomeLabel={getOutcomeLabel}
              getBookmakerDisplayName={getBookmakerDisplayName}
              onCalculatorClick={(e) => handleCalculatorClick(marginKey, e)}
              showLiveDemoMarginInfo={showLiveDemoMarginInfo}
              surebetId={surebet.id}
              t={t}
            />
          ))}
        </div>
      )}

      {/* No margins available */}
      {marginTypes.length === 0 && (
        <div className="surebet-item__no-margins">
          <p>{t('surebets.card.noMarginData')}</p>
        </div>
      )}
    </article>
  );
};

SurebetItem.propTypes = {
  surebet: PropTypes.shape({
    id: PropTypes.number.isRequired,
    event: PropTypes.shape({
      id: PropTypes.number.isRequired,
      date: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
      teamOne: PropTypes.string.isRequired,
      teamTwo: PropTypes.string.isRequired,
      championship: PropTypes.string.isRequired,
      sport: PropTypes.string.isRequired
    }).isRequired,
    marginFlag: PropTypes.bool.isRequired,
    margins: PropTypes.object.isRequired
  }).isRequired,
  onCalculatorClick: PropTypes.func,
  onClick: PropTypes.func,
  className: PropTypes.string,
  showLiveDemoMarginInfo: PropTypes.bool,
};

/**
 * MarginRow - Individual margin row component
 * Displays market type, coefficients, profit, and calculator button
 */
const MarginRow = React.memo(({
  marginKey,
  marginData,
  getMarginTypeName,
  getOutcomeLabel,
  getBookmakerDisplayName,
  onCalculatorClick,
  showLiveDemoMarginInfo,
  surebetId,
  t,
}) => {
  const isProfitable = marginData.value > 0;
  const liveDemoTooltipId = `live-demo-margin-tooltip-${surebetId}-${marginKey}`;

  const handleInfoClick = (event) => {
    event.stopPropagation();
  };

  return (
    <div
      className={`surebet-item__margin-row ${isProfitable ? 'surebet-item__margin-row--profitable' : ''}`}
      role="row"
    >
      {/* Market Type */}
      <div className="surebet-item__market-type" role="cell">
        <span className="surebet-item__market-name">
          {getMarginTypeName(marginKey)}
        </span>
      </div>

      {/* Coefficients - bookmaker names only (same logic as filter) */}
      <div className="surebet-item__coefficients" role="cell">
        {marginData.coefficients && marginData.coefficients.length > 0 ? (
          marginData.coefficients.map((coef, index) => (
            <div key={index} className="surebet-item__coefficient">
              <div className="surebet-item__bookmaker">
                <span
                  className="surebet-item__bookmaker-name"
                  aria-label={getBookmakerDisplayName(coef.bookmaker)}
                  title={getBookmakerDisplayName(coef.bookmaker)}
                >
                  {getBookmakerDisplayName(coef.bookmaker)}
                </span>
              </div>

              {/* Coefficient Details */}
              <div className="surebet-item__coefficient-info">
                <span className="surebet-item__coefficient-label">
                  {getOutcomeLabel(marginKey, coef.type)}:
                </span>
                <span className="surebet-item__coefficient-value">
                  {NumberUtils.formatOdds(coef.value, { decimals: 2 })}
                </span>
              </div>
            </div>
          ))
        ) : (
          <span className="surebet-item__no-coefficients">
            {t('surebets.card.noCoefficients')}
          </span>
        )}
      </div>

      {/* Profit Percentage */}
      <div className="surebet-item__profit" role="cell">
        <div className="surebet-item__profit-inner">
          <span
            className={`surebet-item__profit-value ${isProfitable ? 'surebet-item__profit-value--positive' : 'surebet-item__profit-value--negative'}`}
            aria-label={`${t('surebets.card.profit')}: ${NumberUtils.formatPercentage(marginData.value, { decimals: 2 })}`}
          >
            {NumberUtils.formatPercentage(marginData.value, { decimals: 2 })}
          </span>
          {showLiveDemoMarginInfo && (
            <span className="surebet-item__live-demo-info">
              <button
                type="button"
                className="surebet-item__live-demo-info-trigger"
                aria-label={t(
                  'landing.liveDemo.marginInfoAriaLabel',
                  'Информация за Live Demo марж'
                )}
                aria-describedby={liveDemoTooltipId}
                onClick={handleInfoClick}
              ></button>
              <span
                id={liveDemoTooltipId}
                role="tooltip"
                className="surebet-item__live-demo-info-tooltip"
              >
                {t(
                  'landing.liveDemo.marginInfoTooltip',
                  'Този сигнал е част от нашето Live Demo. Данните се предоставят с илюстративна цел и отразяват реалната функционалност на платформата. За пълен достъп до всички анализи, активирайте Premium абонамент.'
                )}
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Calculator Button */}
      <div className="surebet-item__actions" role="cell">
        <button
          className="surebet-item__calculator-btn"
          onClick={onCalculatorClick}
          aria-label={`${t('surebets.card.calculatorButton')} - ${getMarginTypeName(marginKey)}`}
          type="button"
        >
          {t('surebets.card.calculatorButton')}
        </button>
      </div>
    </div>
  );
});

MarginRow.displayName = 'MarginRow';

MarginRow.propTypes = {
  marginKey: PropTypes.string.isRequired,
  marginData: PropTypes.shape({
    value: PropTypes.number.isRequired,
    coefficients: PropTypes.arrayOf(PropTypes.shape({
      bookmaker: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired
    })).isRequired
  }).isRequired,
  getMarginTypeName: PropTypes.func.isRequired,
  getOutcomeLabel: PropTypes.func.isRequired,
  getBookmakerDisplayName: PropTypes.func.isRequired,
  onCalculatorClick: PropTypes.func.isRequired,
  showLiveDemoMarginInfo: PropTypes.bool,
  surebetId: PropTypes.number,
  t: PropTypes.func.isRequired,
};

export default SurebetItem;

